import { useEffect, useState } from 'react';
import api, { descargarReporte } from '../api/client.js';
import { useAuth } from '../context/AuthContext.jsx';
import { useToast } from '../context/ToastContext.jsx';
import { crc, fechaHora, hoy, METODOS_PAGO } from '../utils/format.js';
import { PageHeader, Modal, Loading, EmptyState } from '../components/UI.jsx';
import { Icon } from '../components/Icons.jsx';
import ComprobanteView from '../components/ComprobanteView.jsx';
import FilterBar from '../components/FilterBar.jsx';

const TIPO_LABEL = {
  ticket: 'Ticket',
  tiquete_electronico: 'Tiquete E.',
  factura_electronica: 'Factura E.',
  factura_exportacion: 'Exportacion',
  proforma: 'Proforma',
};

const FE_COLOR = {
  aceptado: 'var(--success, #16a34a)',
  enviado: '#d97706',
  generado: '#d97706',
  rechazado: 'var(--danger)',
  error: 'var(--danger)',
};
const FE_LABEL = {
  aceptado: 'Aceptada',
  enviado: 'Enviada',
  generado: 'Sin enviar',
  rechazado: 'Rechazada',
  error: 'Error',
  inactiva: 'FE apagada',
};

function inicioMes() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

export default function Ventas() {
  const { esGerente } = useAuth();
  const toast = useToast();
  const [ventas, setVentas] = useState(null);
  const [totales, setTotales] = useState(null);
  const [desde, setDesde] = useState(inicioMes());
  const [hasta, setHasta] = useState(hoy());
  const [q, setQ] = useState('');
  const [estado, setEstado] = useState('');
  const [tipo, setTipo] = useState('');
  const [feEstado, setFeEstado] = useState('');
  const [metodo, setMetodo] = useState('');
  const [detalle, setDetalle] = useState(null);

  const cargar = () => {
    const params = {};
    if (desde) params.desde = desde;
    if (hasta) params.hasta = hasta;
    if (q) params.q = q;
    if (estado) params.estado = estado;
    if (tipo) params.tipo = tipo;
    if (feEstado) params.fe_estado = feEstado;
    if (metodo) params.metodo_pago = metodo;
    api.get('/ventas', { params }).then((r) => {
      const d = r.data.data;
      if (Array.isArray(d)) {
        setVentas(d);
        setTotales(null);
      } else {
        setVentas(d.items || []);
        setTotales(d.totales || null);
      }
    });
  };
  useEffect(cargar, [desde, hasta, q, estado, tipo, feEstado, metodo]);

  const limpiar = () => {
    setDesde(inicioMes());
    setHasta(hoy());
    setQ('');
    setEstado('');
    setTipo('');
    setFeEstado('');
    setMetodo('');
  };

  const verDetalle = async (v) => {
    const { data } = await api.get(`/ventas/${v.id}`);
    setDetalle(data.data);
  };

  const anular = async (v) => {
    if (!confirm(`Anular la venta ${v.numero}? Se devolvera el stock.`)) return;
    try {
      await api.post(`/ventas/${v.id}/anular`);
      toast.success('Venta anulada');
      cargar();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error');
    }
  };

  const esElectronica = (v) =>
    ['factura_electronica', 'tiquete_electronico', 'factura_exportacion'].includes(v.tipo_comprobante);

  const descargarXml = async (v) => {
    try {
      await descargarReporte(`/ventas/${v.id}/xml`, `${v.fe_clave || v.numero}.xml`);
    } catch {
      toast.error('Esta venta no tiene XML generado');
    }
  };

  const reenviar = async (v) => {
    try {
      const { data } = await api.post(`/ventas/${v.id}/fe`);
      if (data.data.estado === 'enviado') toast.success('Enviada a Hacienda');
      else toast.error(data.data.respuesta || `Quedo en estado ${data.data.estado}`);
      cargar();
    } catch (err) {
      toast.error(err.response?.data?.message || 'No se pudo enviar');
    }
  };

  const resumen = totales
    ? `${totales.cantidad} venta(s) · Total ${crc(totales.total)} · IVA ${crc(totales.impuesto)}`
    : ventas
      ? `${ventas.length} venta(s)`
      : '';

  return (
    <div>
      <PageHeader title="Ventas" subtitle="Historial de transacciones" />

      <FilterBar
        desde={desde}
        hasta={hasta}
        onDesde={setDesde}
        onHasta={setHasta}
        busqueda={q}
        onBusqueda={setQ}
        showBusqueda
        placeholderBusqueda="Numero, cliente, clave…"
        onLimpiar={limpiar}
        resumen={resumen}
      >
        <div className="field" style={{ margin: 0, minWidth: 130 }}>
          <label>Estado</label>
          <select className="select" value={estado} onChange={(e) => setEstado(e.target.value)}>
            <option value="">Todos</option>
            <option value="completada">Completada</option>
            <option value="anulada">Anulada</option>
          </select>
        </div>
        <div className="field" style={{ margin: 0, minWidth: 140 }}>
          <label>Tipo</label>
          <select className="select" value={tipo} onChange={(e) => setTipo(e.target.value)}>
            <option value="">Todos</option>
            <option value="ticket">Ticket</option>
            <option value="tiquete_electronico">Tiquete E.</option>
            <option value="factura_electronica">Factura E.</option>
            <option value="factura_exportacion">Exportacion</option>
            <option value="proforma">Proforma</option>
          </select>
        </div>
        <div className="field" style={{ margin: 0, minWidth: 130 }}>
          <label>Hacienda</label>
          <select className="select" value={feEstado} onChange={(e) => setFeEstado(e.target.value)}>
            <option value="">Todos</option>
            <option value="enviado">Enviada</option>
            <option value="aceptado">Aceptada</option>
            <option value="generado">Sin enviar</option>
            <option value="rechazado">Rechazada</option>
            <option value="error">Error</option>
          </select>
        </div>
        <div className="field" style={{ margin: 0, minWidth: 130 }}>
          <label>Pago</label>
          <select className="select" value={metodo} onChange={(e) => setMetodo(e.target.value)}>
            <option value="">Todos</option>
            {METODOS_PAGO.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
        </div>
      </FilterBar>

      {!ventas ? (
        <Loading />
      ) : ventas.length === 0 ? (
        <EmptyState icon="ventas" titulo="Sin ventas" texto="Aun no hay ventas en este periodo." />
      ) : (
        <div className="card table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>Numero</th>
                <th>Fecha</th>
                <th>Cliente</th>
                <th>Tipo</th>
                <th className="text-right">Total</th>
                <th className="text-center">Estado</th>
                <th>Hacienda</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {ventas.map((v) => (
                <tr key={v.id}>
                  <td style={{ fontWeight: 600 }}>{v.numero}</td>
                  <td className="muted" style={{ fontSize: 13 }}>
                    {fechaHora(v.fecha)}
                  </td>
                  <td>{v.cliente_nombre || <span className="muted">Contado</span>}</td>
                  <td>
                    <span className="badge" style={{ background: 'var(--accent-soft)', color: 'var(--accent-texto)' }}>
                      {TIPO_LABEL[v.tipo_comprobante]}
                    </span>
                  </td>
                  <td className="text-right mono" style={{ fontWeight: 600 }}>
                    {crc(v.total)}
                  </td>
                  <td className="text-center">
                    {v.estado === 'anulada' ? (
                      <span className="badge badge-danger">Anulada</span>
                    ) : (
                      <span className="badge badge-success">Completada</span>
                    )}
                  </td>
                  <td>
                    {esElectronica(v) ? (
                      <span
                        style={{
                          color: FE_COLOR[v.fe_estado] || 'var(--text-soft)',
                          fontWeight: 600,
                          fontSize: 13,
                        }}
                      >
                        {FE_LABEL[v.fe_estado] || v.fe_estado || '—'}
                      </span>
                    ) : (
                      <span className="muted" style={{ fontSize: 12 }}>
                        No aplica
                      </span>
                    )}
                  </td>
                  <td>
                    <div className="flex gap-sm" style={{ justifyContent: 'flex-end' }}>
                      {esElectronica(v) && v.fe_xml && (
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={() => descargarXml(v)}
                          title="Descargar XML"
                        >
                          <Icon.download width={15} height={15} />
                        </button>
                      )}
                      {esGerente &&
                        esElectronica(v) &&
                        ['generado', 'error', 'rechazado'].includes(v.fe_estado) && (
                          <button
                            className="btn btn-ghost btn-sm"
                            onClick={() => reenviar(v)}
                            title="Reintentar envio a Hacienda"
                          >
                            Reenviar
                          </button>
                        )}
                      <button className="btn btn-ghost btn-sm" onClick={() => verDetalle(v)} title="Ver">
                        <Icon.print width={15} height={15} />
                      </button>
                      {esGerente && v.estado !== 'anulada' && (
                        <button
                          className="btn btn-sm"
                          style={{ color: 'var(--danger)' }}
                          onClick={() => anular(v)}
                          title="Anular"
                        >
                          <Icon.close width={15} height={15} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal open={!!detalle} onClose={() => setDetalle(null)} title="Detalle de venta" width={420}>
        {detalle && <ComprobanteView venta={detalle} onClose={() => setDetalle(null)} />}
      </Modal>
    </div>
  );
}
